import { Product } from "@transfr-inc/dashboard-components/constants";

import { Role } from "@models/role";

export const getClassroomUsersMastery = ({
  insightsService,
  simulationsService,
  classroom,
  productId,
}) => {
  const { classroomId } = classroom;

  const setTopCareers = async (masteryInfo) => {
    const simulations = (await simulationsService.getAllSimulations()).map(
      (s) => [s.learningExperienceId, s]
    );
    const simulationsMap = new Map(simulations);

    for (const m of masteryInfo) {
      const topCareers = m.topExperiences.map((e) => {
        const sim = simulationsMap.get(e);
        return sim?.occupationName;
      });

      const careersSet = new Set(topCareers);
      m.topCareers = [...careersSet.values()].filter((c) => c);
      m.totalTopCareers = careersSet.size;
    }

    return masteryInfo;
  };

  const getCeClassroomUsersMastery = async (classroomId) => {
    const masteryInfo = await insightsService.getCeClassroomExplorersMastery(
      classroomId
    );
    return setTopCareers(masteryInfo);
  };

  return [Product.TS, Product.PROG].includes(productId)
    ? insightsService.getClassroomTraineesMastery(classroomId)
    : getCeClassroomUsersMastery(classroomId);
};

/**
 * One career's per-explorer ratings, from the endpoint variant the caller's
 * scope answers to. An instructor is always served by the instructor variant,
 * whose data is already the classrooms they teach, so `orgCode` never overrides
 * it. `orgCode` is the organization itself, or the parent code for a multi-org
 * request, which `organizationCodes` narrows to child orgs. `rejectOnError`
 * rejects a failed request instead of resolving to an empty list.
 *
 * @param {{ organizationService: any, orgCode?: string, userId?: string,
 *   classroomIds?: string[], organizationCodes?: string[], userIds?: string[],
 *   role?: string, dateRange?: { startDate?: string, endDate?: string },
 *   learningExperienceId?: string, isMultiOrg?: boolean,
 *   rejectOnError?: boolean }} params
 * @returns {Promise<Array<Object>>}
 */
export const getCareerRatings = ({
  organizationService,
  orgCode,
  userId,
  classroomIds,
  organizationCodes,
  userIds,
  role,
  dateRange,
  learningExperienceId,
  isMultiOrg,
  rejectOnError,
}) => {
  const filters = { dateRange, classroomIds, organizationCodes, userIds };

  if (userId && role === Role.INSTRUCTOR.id) {
    return organizationService.getInstructorCareerRatings(
      userId,
      learningExperienceId,
      filters,
      rejectOnError
    );
  } else if (orgCode && !isMultiOrg) {
    return organizationService.getOrganizationCareerRatings(
      orgCode,
      learningExperienceId,
      filters,
      rejectOnError
    );
  } else if (orgCode) {
    return organizationService.getMultiOrganizationCareerRatings(
      orgCode,
      learningExperienceId,
      filters,
      rejectOnError
    );
  } else {
    return Promise.resolve([]);
  }
};

export const setCareerInfo = async ({ simulationsService, sims = [] }) => {
  const simulations = (await simulationsService.getAllSimulations()).map(
    (s) => [s.learningExperienceId, s]
  );
  const simulationsMap = new Map(simulations);

  return sims
    .map((s) => {
      const career = simulationsMap.get(s.learningExperienceId);
      return (
        career && {
          ...s,
          ...career,
        }
      );
    })
    .filter((c) => c);
};

export const sortByNameFn = (a, b) => {
  const aName = a.name.toLowerCase();
  const bName = b.name.toLowerCase();
  const ratingDiff = b.rating - a.rating;
  if (ratingDiff == 0) {
    return aName < bName ? -1 : 1;
  } else {
    return ratingDiff;
  }
};
